<template>
  <div class="modal-card card">
    <div class="card-header">
      <h4 class="card-header-title">Create Audience</h4>
      <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
    </div>
    <div class="card-body" style="max-height: 70vh; overflow-y: auto;">
      <div v-if="loading" class="text-center py-4">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="mt-2 text-muted">Loading options...</p>
      </div>

      <form v-else @submit.prevent="onSubmit">
        <!-- Audience Name -->
        <div class="mb-3">
          <label class="form-label fw-bold">Audience Name <span class="text-danger">*</span></label>
          <input
            type="text"
            class="form-control"
            v-model="formData.audienceName"
            placeholder="Enter audience name"
            required
          />
        </div>

        <!-- Data Environment -->
        <div class="mb-3">
          <label class="form-label fw-bold">Data Environment <span class="text-danger">*</span></label>
          <select class="form-select" v-model="formData.dataEnvironment" @change="onDataEnvironmentChange" required>
            <option value="">Select data environment...</option>
            <option v-for="env in dataEnvironments" :key="getEnvValue(env)" :value="getEnvValue(env)">
              {{ getEnvLabel(env) }}
            </option>
          </select>
        </div>

        <!-- Dataset -->
        <div class="mb-3">
          <label class="form-label fw-bold">Dataset <span class="text-danger">*</span></label>
          <select class="form-select" v-model="formData.dataset" @change="onDatasetChange" :disabled="!formData.dataEnvironment" required>
            <option value="">Select dataset...</option>
            <option v-for="ds in datasets" :key="getDatasetValue(ds)" :value="getDatasetValue(ds)">
              {{ getDatasetLabel(ds) }}
            </option>
          </select>
        </div>

        <!-- Mode Toggle -->
        <div class="mb-3">
          <label class="form-label fw-bold">Criteria <span class="text-danger">*</span></label>
          <div class="btn-group w-100 mb-3" role="group">
            <button
              type="button"
              class="btn"
              :class="{ 'btn-primary': criterionMode === 'simple', 'btn-outline-primary': criterionMode !== 'simple' }"
              @click="switchMode('simple')"
            >
              <i class="fa fa-list me-2"></i>
              Simple
            </button>
            <button
              type="button"
              class="btn"
              :class="{ 'btn-primary': criterionMode === 'advanced', 'btn-outline-primary': criterionMode !== 'advanced' }"
              @click="switchMode('advanced')"
            >
              <i class="fa fa-filter me-2"></i>
              Advanced
            </button>
            <button
              type="button"
              class="btn"
              :class="{ 'btn-primary': criterionMode === 'json', 'btn-outline-primary': criterionMode !== 'json' }"
              @click="switchMode('json')"
            >
              <i class="fa fa-code me-2"></i>
              JSON
            </button>
          </div>
        </div>

        <!-- Loading Schema -->
        <div v-if="loadingSchema" class="text-center py-3">
          <div class="spinner-border spinner-border-sm text-primary" role="status">
            <span class="visually-hidden">Loading columns...</span>
          </div>
          <span class="ms-2 text-muted">Loading columns...</span>
        </div>

        <!-- Simple Mode -->
        <div v-else-if="criterionMode === 'simple'">
          <SimplifiedCriterion
            :criteria="formData.criteria"
            :columns="availableColumns"
            :column-values="columnValues"
            :loading-values="loadingValues"
            @update:criteria="formData.criteria = $event"
            @columnSelected="onColumnSelected"
          />
        </div>

        <!-- Advanced Mode -->
        <div v-else-if="criterionMode === 'advanced'">
          <div class="mb-3 p-3 bg-light rounded">
            <div class="form-check form-switch mb-2">
              <input
                class="form-check-input"
                type="checkbox"
                id="negateSwitch"
                v-model="advancedOptions.negated"
              />
              <label class="form-check-label" for="negateSwitch">
                <i class="fa fa-ban me-2"></i>
                Generate count NOT matching criterion
              </label>
            </div>
            <div>
              <label class="form-label fw-bold">
                <i class="fa fa-layer-group me-2"></i>
                Match Operator
              </label>
              <select class="form-select" v-model="advancedOptions.operator">
                <option value="OR">Match ANY of the criterion groups below</option>
                <option value="AND">Match ALL of the criterion groups below</option>
              </select>
            </div>
          </div>

          <div class="criterion-groups-container">
            <div
              v-for="(group, groupIndex) in criterionGroups"
              :key="groupIndex"
              class="criterion-group-wrapper mb-3"
            >
              <CriterionGroup
                :group-index="groupIndex"
                :criteria="group.criteria"
                :group-operator="group.operator"
                :groups="group.groups || []"
                :columns="availableColumns"
                :column-values="columnValues"
                :loading-values="groupLoadingValues[groupIndex] || {}"
                @update:criteria="updateGroupCriteria(groupIndex, $event)"
                @update:groupOperator="updateGroupOperator(groupIndex, $event)"
                @update:groups="updateGroupNestedGroups(groupIndex, $event)"
                @columnSelected="onGroupColumnSelected"
                @deleteGroup="deleteGroup(groupIndex)"
              />

              <!-- Group operator divider -->
              <div v-if="groupIndex < criterionGroups.length - 1" class="group-operator-divider my-3">
                <div class="operator-badge">
                  <i :class="advancedOptions.operator === 'AND' ? 'fa fa-plus' : 'fa fa-circle'" class="me-2"></i>
                  {{ advancedOptions.operator }}
                </div>
              </div>
            </div>
          </div>

          <div class="text-center mt-3">
            <button type="button" class="btn btn-outline-primary" @click="addGroup">
              <i class="fa fa-plus me-2"></i>
              Add Criterion Group
            </button>
          </div>
        </div>

        <!-- JSON Mode -->
        <div v-else-if="criterionMode === 'json'">
          <div class="json-editor-header mb-2 d-flex justify-content-between align-items-center">
            <small class="text-muted">
              <i class="fa fa-info-circle me-1"></i>
              Paste or edit criterion JSON directly
            </small>
            <div class="btn-group btn-group-sm">
              <button type="button" class="btn btn-outline-secondary" @click="formatJson">
                <i class="fa fa-align-left me-1"></i>
                Format
              </button>
              <button type="button" class="btn btn-outline-primary" @click="validateJson">
                <i class="fa fa-check me-1"></i>
                Validate
              </button>
            </div>
          </div>
          <textarea
            v-model="jsonEditorValue"
            class="form-control json-editor"
            :class="{ 'is-invalid': jsonError }"
            rows="12"
            placeholder="Paste your criterion JSON here..."
            spellcheck="false"
          ></textarea>
          <div v-if="jsonError" class="invalid-feedback d-block">
            {{ jsonError }}
          </div>
          <div v-if="jsonSuccess" class="text-success small mt-2">
            <i class="fa fa-check-circle me-1"></i>
            JSON is valid
          </div>
        </div>

        <!-- Count Result -->
        <div v-if="countResult !== null" class="alert alert-success mt-3">
          <strong>Audience Count:</strong> {{ formatNumber(countResult) }}
        </div>
      </form>

      <div v-if="error" class="alert alert-danger mt-3">
        {{ error }}
      </div>
    </div>
    <div class="card-footer">
      <button
        type="button"
        class="btn btn-outline-primary"
        @click="runCountQuery"
        :disabled="!isFormValid || runningCount"
      >
        <span v-if="runningCount" class="spinner-border spinner-border-sm me-2" />
        <i v-else class="fa fa-calculator me-1"></i>
        Validate Audience
      </button>
      <button
        type="button"
        class="btn btn-primary ms-2"
        @click="onSubmit"
        :disabled="!canSave || saving"
      >
        <span v-if="saving" class="spinner-border spinner-border-sm me-2" />
        Save Audience
      </button>
      <button type="button" class="btn btn-secondary ms-2" data-bs-dismiss="modal">
        Cancel
      </button>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import SimplifiedCriterion from '../audience/SimplifiedCriterion.vue'
import CriterionGroup from '../audience/CriterionGroup.vue'
import {
  parseSimpleCriteria,
  parseAdvancedCriteria,
  getCriterionNestingDepth,
  buildSimpleCriterionJson,
  buildAdvancedCriterionJson,
  prePopulateColumnValues,
  prePopulateColumnValuesFromGroups,
  extractValuesFromHistogram,
  createEmptyCriterionGroup,
  createEmptySimpleCriterion,
} from '../../utils/criterionUtils'

export default {
  name: 'AddAudience',
  components: {
    SimplifiedCriterion,
    CriterionGroup,
  },
  props: {
    modalData: Object,
  },
  data() {
    return {
      loading: true,
      loadingSchema: false,
      saving: false,
      runningCount: false,
      error: null,
      dataEnvironments: [],
      datasets: [],
      availableColumns: [],
      columnValues: {},
      loadingValues: {},
      countResult: null,
      criterionMode: 'simple', // 'simple', 'advanced', 'json'
      formData: {
        audienceName: '',
        dataEnvironment: '',
        dataset: '',
        criteria: [{ column: null, values: [] }],
      },
      // Advanced mode
      advancedOptions: {
        operator: 'OR',
        negated: false,
      },
      criterionGroups: [
        {
          operator: 'AND',
          criteria: [{
            column: null,
            condition: 'IN',
            values: [],
            lowerbound: null,
            upperbound: null,
            singleValue: null,
          }],
          groups: [],
        }
      ],
      groupLoadingValues: {},
      // JSON mode
      jsonEditorValue: '',
      jsonError: null,
      jsonSuccess: false,
    }
  },
  computed: {
    isFormValid() {
      if (!this.formData.audienceName.trim()) return false
      if (!this.formData.dataEnvironment) return false
      if (!this.formData.dataset) return false

      if (this.criterionMode === 'simple') {
        return this.formData.criteria.some(c => c.column && c.values && c.values.length > 0)
      } else if (this.criterionMode === 'advanced') {
        return this.criterionGroups.some(group =>
          group.criteria.some(c => {
            if (!c.column) return false
            if (c.condition === 'IN') return c.values && c.values.length > 0
            if (c.condition === 'BETWEEN') return c.lowerbound && c.upperbound
            if (c.condition === 'EQUAL') return c.singleValue
            return false
          })
        )
      } else if (this.criterionMode === 'json') {
        try {
          const parsed = JSON.parse(this.jsonEditorValue)
          return parsed && (parsed.criterion || parsed.operands)
        } catch {
          return false
        }
      }
      return false
    },
    canSave() {
      return this.isFormValid && this.countResult !== null
    },
  },
  methods: {
    ...mapActions({
      fetchDataEnvironments: 'audience/fetchDataEnvironments',
      fetchSchema: 'audience/fetchSchema',
      fetchColumnValues: 'audience/fetchColumnValues',
      createAudience: 'audience/createAudience',
      hideModal: 'hideModal',
      addToast: 'addToast',
    }),
    async loadDataEnvironments() {
      this.loading = true
      try {
        this.dataEnvironments = await this.fetchDataEnvironments()
      } catch (error) {
        this.error = 'Failed to load data environments'
      } finally {
        this.loading = false
      }
    },
    onDataEnvironmentChange() {
      this.formData.dataset = ''
      this.datasets = []
      this.availableColumns = []
      this.columnValues = {}
      this.countResult = null
      this.resetCriteria()

      if (!this.formData.dataEnvironment) return

      const selectedEnv = this.dataEnvironments.find(env =>
        this.getEnvValue(env) === this.formData.dataEnvironment
      )
      if (selectedEnv && selectedEnv.datasets) {
        this.datasets = selectedEnv.datasets
      }
    },
    async onDatasetChange() {
      this.availableColumns = []
      this.columnValues = {}
      this.countResult = null
      this.resetCriteria()

      if (!this.formData.dataset) return

      this.loadingSchema = true
      try {
        const schemaResult = await this.fetchSchema({
          dataEnvironmentId: this.formData.dataEnvironment,
          dataset: this.formData.dataset
        })
        const columns = schemaResult?.schema?.columns || schemaResult?.columns || []
        this.availableColumns = columns.map(c => c.name || c)
      } catch (error) {
        this.error = 'Failed to load schema'
      } finally {
        this.loadingSchema = false
      }
    },
    async onColumnSelected({ column, index }) {
      if (!column || this.columnValues[column]) return

      this.error = null

      this.loadingValues = { ...this.loadingValues, [index]: true }
      try {
        const result = await this.fetchColumnValues({
          dataEnvironmentId: this.formData.dataEnvironment,
          dataset: this.formData.dataset,
          column
        })
        const values = extractValuesFromHistogram(result)
        console.log('Extracted column values for', column, ':', values)
        this.columnValues = { ...this.columnValues, [column]: values }
      } catch (error) {
        console.error('Failed to load column values:', error)
        this.error = error.response?.data?.message || error.message || `Failed to load values for "${column}"`
        this.columnValues = { ...this.columnValues, [column]: [] }
      } finally {
        this.loadingValues = { ...this.loadingValues, [index]: false }
      }
    },
    async onGroupColumnSelected({ column, groupIndex, criterionIndex }) {
      if (!column || this.columnValues[column]) return

      this.error = null

      const newGroupLoadingValues = { ...this.groupLoadingValues }
      if (!newGroupLoadingValues[groupIndex]) {
        newGroupLoadingValues[groupIndex] = {}
      }
      newGroupLoadingValues[groupIndex] = { ...newGroupLoadingValues[groupIndex], [criterionIndex]: true }
      this.groupLoadingValues = newGroupLoadingValues

      try {
        const result = await this.fetchColumnValues({
          dataEnvironmentId: this.formData.dataEnvironment,
          dataset: this.formData.dataset,
          column
        })
        const values = extractValuesFromHistogram(result)
        console.log('Extracted column values for', column, ':', values)
        this.columnValues = { ...this.columnValues, [column]: values }
      } catch (error) {
        console.error('Failed to load column values:', error)
        this.error = error.response?.data?.message || error.message || `Failed to load values for "${column}"`
        this.columnValues = { ...this.columnValues, [column]: [] }
      } finally {
        const finalGroupLoadingValues = { ...this.groupLoadingValues }
        finalGroupLoadingValues[groupIndex] = { ...finalGroupLoadingValues[groupIndex], [criterionIndex]: false }
        this.groupLoadingValues = finalGroupLoadingValues
      }
    },
    switchMode(mode) {
      // When switching TO JSON mode, sync current criteria to JSON
      if (mode === 'json' && this.criterionMode !== 'json') {
        this.syncToJson()
      }
      // When switching FROM JSON mode to Simple or Advanced, parse JSON and update criteria
      if (this.criterionMode === 'json' && mode !== 'json') {
        this.applyJsonToCriteria(mode)
      }
      this.criterionMode = mode
      this.jsonError = null
      this.jsonSuccess = false
    },
    applyJsonToCriteria(targetMode) {
      try {
        const parsed = JSON.parse(this.jsonEditorValue)
        if (!parsed || !parsed.criterion) {
          console.warn('No criterion found in JSON')
          return
        }

        const criterion = parsed.criterion

        // Check for negation
        let actualCriterion = criterion
        if (criterion.operator === 'not' && criterion.operand) {
          this.advancedOptions.negated = true
          actualCriterion = criterion.operand
        } else {
          this.advancedOptions.negated = false
        }

        if (targetMode === 'simple') {
          // Parse as simple criteria - flatten all nested criteria
          const newCriteria = parseSimpleCriteria(actualCriterion)
          this.columnValues = prePopulateColumnValues(newCriteria, this.columnValues)
          this.formData.criteria = newCriteria
        } else if (targetMode === 'advanced') {
          // Check nesting depth - if > 2, warn that structure will be simplified
          const depth = getCriterionNestingDepth(actualCriterion)
          if (depth > 2) {
            console.warn(`Complex nested criterion (depth: ${depth}) will be simplified for Advanced view`)
          }
          // Parse as advanced criteria - will flatten deeply nested structures
          const parsedAdvanced = parseAdvancedCriteria(actualCriterion)
          this.advancedOptions.operator = parsedAdvanced.operator
          this.criterionGroups = parsedAdvanced.groups
          this.columnValues = prePopulateColumnValuesFromGroups(this.criterionGroups, this.columnValues)
        }
      } catch (e) {
        console.error('Failed to parse JSON for mode switch:', e)
        this.jsonError = 'Invalid JSON: ' + e.message
      }
    },
    syncToJson() {
      let criterionObj
      if (this.criterionMode === 'simple') {
        criterionObj = buildSimpleCriterionJson(this.formData.criteria)
      } else {
        criterionObj = buildAdvancedCriterionJson(this.criterionGroups, {
          operator: this.advancedOptions.operator,
          negated: this.advancedOptions.negated
        })
      }
      this.jsonEditorValue = JSON.stringify(criterionObj, null, 2)
    },
    formatJson() {
      try {
        const parsed = JSON.parse(this.jsonEditorValue)
        this.jsonEditorValue = JSON.stringify(parsed, null, 2)
        this.jsonError = null
      } catch (e) {
        this.jsonError = 'Invalid JSON: ' + e.message
      }
    },
    validateJson() {
      this.jsonError = null
      this.jsonSuccess = false
      try {
        const parsed = JSON.parse(this.jsonEditorValue)
        if (!parsed.criterion) {
          this.jsonError = 'JSON must contain a "criterion" property'
          return
        }
        this.jsonSuccess = true
        setTimeout(() => { this.jsonSuccess = false }, 3000)
      } catch (e) {
        this.jsonError = 'Invalid JSON: ' + e.message
      }
    },
    resetCriteria() {
      this.formData.criteria = [createEmptySimpleCriterion()]
      this.criterionGroups = [createEmptyCriterionGroup()]
      this.jsonEditorValue = ''
    },
    updateGroupCriteria(groupIndex, criteria) {
      this.criterionGroups[groupIndex].criteria = criteria
    },
    updateGroupOperator(groupIndex, operator) {
      this.criterionGroups[groupIndex].operator = operator
    },
    updateGroupNestedGroups(groupIndex, groups) {
      if (!this.criterionGroups[groupIndex].groups) {
        this.$set(this.criterionGroups[groupIndex], 'groups', [])
      }
      this.criterionGroups[groupIndex].groups = groups
    },
    addGroup() {
      this.criterionGroups.push(createEmptyCriterionGroup())
    },
    deleteGroup(groupIndex) {
      if (this.criterionGroups.length > 1) {
        this.criterionGroups.splice(groupIndex, 1)
      }
    },
    buildCriterionJson() {
      if (this.criterionMode === 'json') {
        return this.jsonEditorValue
      }

      if (this.criterionMode === 'simple') {
        return JSON.stringify(buildSimpleCriterionJson(this.formData.criteria))
      } else {
        return JSON.stringify(buildAdvancedCriterionJson(this.criterionGroups, {
          operator: this.advancedOptions.operator,
          negated: this.advancedOptions.negated
        }))
      }
    },
    async runCountQuery() {
      if (!this.isFormValid) return

      this.runningCount = true
      this.error = null
      this.countResult = null

      try {
        let criterion
        if (this.criterionMode === 'json') {
          const parsed = JSON.parse(this.jsonEditorValue)
          criterion = parsed.criterion
        } else {
          criterion = JSON.parse(this.buildCriterionJson()).criterion
        }

        const queryBody = {
          dataset: this.formData.dataset,
          criterion
        }

        await this.$store.dispatch('audience/runCountQuery', {
          dataEnvironmentId: this.formData.dataEnvironment,
          queryBody
        })

        this.countResult = this.$store.state.audience.audienceCount
      } catch (error) {
        this.error = error.response?.data?.message || error.message || 'Failed to run count query'
      } finally {
        this.runningCount = false
      }
    },
    async onSubmit() {
      if (!this.canSave) return

      this.saving = true
      this.error = null

      try {
        const audienceData = {
          audience_name: this.formData.audienceName.trim(),
          audsum_data_environment: this.formData.dataEnvironment,
          audsum_dataset: this.formData.dataset,
          audience_criterion_json: this.buildCriterionJson(),
          parent_audiences: '',
        }

        await this.createAudience(audienceData)
        this.addToast({ type: 'success', message: 'Audience created successfully' })
        this.hideModal()
      } catch (error) {
        this.error = error.response?.data?.message || error.message || 'Failed to create audience'
      } finally {
        this.saving = false
      }
    },
    formatNumber(num) {
      if (num == null) return '-'
      return num.toLocaleString()
    },
    getEnvValue(env) {
      if (typeof env === 'string') return env
      return env.name || env.data_environment || env.id || ''
    },
    getEnvLabel(env) {
      if (typeof env === 'string') return env
      return env.name || env.data_environment || env.id || 'Unknown'
    },
    getDatasetValue(ds) {
      if (typeof ds === 'string') return ds
      return ds.name || ds.dataset || ds.id || ''
    },
    getDatasetLabel(ds) {
      if (typeof ds === 'string') return ds
      return ds.name || ds.dataset || ds.id || 'Unknown'
    },
    async applyPrefillData(prefillData) {
      // Set data environment
      if (prefillData.audsum_data_environment) {
        this.formData.dataEnvironment = prefillData.audsum_data_environment

        // Load datasets for this environment
        const selectedEnv = this.dataEnvironments.find(env =>
          this.getEnvValue(env) === this.formData.dataEnvironment
        )
        if (selectedEnv && selectedEnv.datasets) {
          this.datasets = selectedEnv.datasets
        }
      }

      // Set dataset and load schema
      if (prefillData.audsum_dataset) {
        this.formData.dataset = prefillData.audsum_dataset

        // Load schema for this dataset
        if (this.formData.dataEnvironment && this.formData.dataset) {
          this.loadingSchema = true
          try {
            const schemaResult = await this.fetchSchema({
              dataEnvironmentId: this.formData.dataEnvironment,
              dataset: this.formData.dataset
            })
            const columns = schemaResult?.schema?.columns || schemaResult?.columns || []
            this.availableColumns = columns.map(c => c.name || c)
          } catch (error) {
            console.error('Failed to load schema for prefill:', error)
          } finally {
            this.loadingSchema = false
          }
        }
      }

      // Set criterion if provided
      if (prefillData.criterion) {
        const criterionObj = prefillData.criterion
        const criterion = criterionObj.criterion || criterionObj

        // Parse the criterion and populate the simple criteria view
        if (criterion) {
          const parsedCriteria = parseSimpleCriteria(criterion)
          this.formData.criteria = parsedCriteria

          // Pre-populate column values so they appear in the multiselects
          this.columnValues = prePopulateColumnValues(parsedCriteria, this.columnValues)

          // Also set the JSON editor value for JSON mode
          this.jsonEditorValue = JSON.stringify(criterionObj, null, 2)
        }
      }
    },
  },
  async mounted() {
    await this.loadDataEnvironments()

    // Handle prefill data from dashboard or other sources
    if (this.modalData?.prefillData) {
      await this.applyPrefillData(this.modalData.prefillData)
    }
  },
}
</script>

<style scoped>
.form-label {
  font-size: 0.875rem;
}

.btn-group .btn {
  flex: 1;
}

.json-editor {
  font-family: 'Courier New', Courier, monospace;
  font-size: 0.875rem;
  line-height: 1.5;
  background-color: #1e1e1e;
  color: #d4d4d4;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  resize: vertical;
  tab-size: 2;
}

.json-editor:focus {
  outline: none;
  border-color: var(--bs-primary);
  box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
}

.json-editor.is-invalid {
  border-color: var(--bs-danger);
}

.group-operator-divider {
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
}

.group-operator-divider::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  height: 2px;
  background: linear-gradient(to right, transparent, #dee2e6 20%, #dee2e6 80%, transparent);
}

.operator-badge {
  background: linear-gradient(135deg, var(--bs-primary, #2C7BE5), #0056b3);
  color: white;
  padding: 0.5rem 1.5rem;
  border-radius: 2rem;
  font-weight: 600;
  font-size: 0.875rem;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 12px rgba(13, 110, 253, 0.3);
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
}
</style>
